(function (globalScope, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) module.exports = api;
  if (globalScope) globalScope.CernirCustomStrapCommerce = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const DATA = Object.freeze({
  "version": "2026-09-v4",
  "currencyCode": "USD",
  "priceStepCents": 500,
  "variantPriceMinCents": 500,
  "variantPriceMaxCents": 40000,
  "materials": [
    {
      "id": "alligator",
      "group": "Exotic Leather",
      "name": "Alligator",
      "priceCents": 14500
    },
    {
      "id": "ostrich-leg",
      "group": "Exotic Leather",
      "name": "Ostrich Leg",
      "priceCents": 12500
    },
    {
      "id": "alran-chevre",
      "group": "Regular Leather",
      "name": "Alran Chevre",
      "priceCents": 7500
    },
    {
      "id": "babele",
      "group": "Regular Leather",
      "name": "Babele",
      "priceCents": 7500
    },
    {
      "id": "buttero",
      "group": "Regular Leather",
      "name": "Buttero",
      "priceCents": 7500
    },
    {
      "id": "epsom",
      "group": "Regular Leather",
      "name": "Epsom",
      "priceCents": 7500
    },
    {
      "id": "pueblo",
      "group": "Regular Leather",
      "name": "Pueblo",
      "priceCents": 7500
    },
    {
      "id": "saffiano",
      "group": "Regular Leather",
      "name": "Saffiano",
      "priceCents": 7500
    },
    {
      "id": "shell-cordovan",
      "group": "Regular Leather",
      "name": "Shell Cordovan",
      "priceCents": 7500
    },
    {
      "id": "togo",
      "group": "Regular Leather",
      "name": "Togo",
      "priceCents": 7500
    }
  ],
  "optionSurchargesCents": {
    "style": {
      "pilot": 1000
    },
    "curvedEnd": {
      "yes": 1000
    },
    "liningMaterial": {
      "calfskin-epsom": 500,
      "goatskin-alran-chevre": 500,
      "alligator-round-scales": 4500
    },
    "padding": {
      "domed": 500,
      "square": 500,
      "full": 1000
    },
    "stitchingStyle": {
      "box-stitches": 500,
      "classic-double-stitches": 1000
    },
    "buckle": {
      "butterfly-silver": 1000,
      "butterfly-yellow-gold": 1000,
      "butterfly-black": 1000
    },
    "noCernirLogo": {
      "true": 2000
    }
  },
  "foldedEdgeRules": {
    "default": {
      "supported": false,
      "surchargeCents": 0
    },
    "families": {
      "alligator": {
        "materialIds": [
          "alligator"
        ],
        "supported": true,
        "surchargeCents": 8000
      },
      "epsom": {
        "materialIds": [
          "epsom"
        ],
        "supported": true,
        "surchargeCents": 5000
      },
      "alran-chevre": {
        "materialIds": [
          "alran-chevre"
        ],
        "supported": true,
        "surchargeCents": 5000
      },
      "togo": {
        "materialIds": [
          "togo"
        ],
        "supported": true,
        "surchargeCents": 5000
      }
    }
  }
});

  // Date of the last change to the order properties or their meaning (2026-10-28: stamped text without accents, 24 mm buckle).
  const CONFIGURATION_VERSION = '2026-10-28';
  const PRICING_VERSION = DATA.version;
  const PRICE_OPTION_FIELDS = Object.freeze(['style', 'curvedEnd', 'liningMaterial', 'padding', 'stitchingStyle', 'edgeFinishing', 'buckle', 'noCernirLogo']);
  const MATERIAL_ALIASES = Object.freeze({
    'alligator-croc': Object.freeze(['alligator', 'alligator-croc']),
    'alligator-round-scales': Object.freeze(['alligator-round-scales']),
    'ostrich-leg': Object.freeze(['ostrich', 'ostrich-leg']),
    'shell-cordovan': Object.freeze(['shell-cordovan']),
    shark: Object.freeze(['shark']),
    'epsom-calf': Object.freeze(['epsom', 'epsom-calf']),
    'togo-calfskin': Object.freeze(['togo', 'togo-calfskin']),
    saffiano: Object.freeze(['saffiano']),
    'alran-chevre-sully': Object.freeze(['alran-sully', 'alran-chevre', 'alran-chevre-sully']),
    'babele-calfskin': Object.freeze(['babele', 'babele-calfskin']),
    buttero: Object.freeze(['buttero']),
    'badalassi-carlo-pueblo': Object.freeze(['pueblo', 'badalassi-carlo-pueblo'])
  });

  function normalize(value) {
    return String(value || '').trim().toLowerCase().normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function parseJsonScript(element, fallback) {
    try {
      return JSON.parse(element?.textContent || '');
    } catch (_error) {
      return fallback;
    }
  }

  function priceTierOptionValue(totalCents) {
    if (!Number.isInteger(totalCents) || totalCents < 0) return null;
    return `$${(totalCents / 100).toFixed(2)}`;
  }

  function findMaterialPrice(material, contract) {
    if (!material) return null;
    const aliases = new Set([normalize(material.id), normalize(material.label), ...(MATERIAL_ALIASES[material.id] || [])]);
    const record = Array.from(contract?.materials || []).find((item) => {
      // A blank metaobject price renders as 0 (Liquid default). Treat 0/negative as "no price" so the order is
      // blocked instead of charging only the surcharges.
      if (item?.active === false || !Number.isInteger(Number(item?.priceCents)) || Number(item?.priceCents) <= 0) return false;
      return aliases.has(normalize(item.id)) || aliases.has(normalize(item.label));
    });
    return record ? Number(record.priceCents) : null;
  }

  function getFoldedEdgeRule(materialId) {
    for (const [family, rule] of Object.entries(DATA.foldedEdgeRules.families)) {
      if (rule.materialIds.includes(materialId)) return { ...rule, family };
    }
    return { ...DATA.foldedEdgeRules.default, family: null, materialIds: [] };
  }

  function getEdgeFinishingSurchargeCents(edgeFinishing, materialId) {
    if (edgeFinishing !== 'folded-edges') return 0;
    const rule = getFoldedEdgeRule(materialId);
    return rule.supported ? rule.surchargeCents : 0;
  }

  function getOptionSurchargeCents(field, optionId, effectiveState) {
    if (field === 'edgeFinishing') return getEdgeFinishingSurchargeCents(optionId, effectiveState?.material);
    const cents = DATA.optionSurchargesCents?.[field]?.[optionId];
    return Number.isInteger(cents) && cents >= 0 ? cents : 0;
  }

  function buildOptionPrices(effectiveState) {
    return PRICE_OPTION_FIELDS.map((field) => Object.freeze({
      field,
      optionId: effectiveState?.[field] || null,
      priceCents: getOptionSurchargeCents(field, effectiveState?.[field], effectiveState)
    }));
  }

  function calculateOptionsPriceCents(effectiveState) {
    return buildOptionPrices(effectiveState).reduce((total, option) => total + option.priceCents, 0);
  }

  function calculatePriceBreakdown(effectiveState, materialCatalog, contract) {
    const material = materialCatalog?.getMaterial(effectiveState?.material);
    const basePriceCents = findMaterialPrice(material, contract);
    if (basePriceCents === null) return null;

    const options = buildOptionPrices(effectiveState);
    const optionsPriceCents = options.reduce((total, option) => total + option.priceCents, 0);

    return Object.freeze({
      pricingVersion: PRICING_VERSION,
      basePriceCents,
      optionsPriceCents,
      totalCents: basePriceCents + optionsPriceCents,
      options: Object.freeze(options)
    });
  }

  function calculateTotalCents(effectiveState, materialCatalog, contract) {
    return calculatePriceBreakdown(effectiveState, materialCatalog, contract)?.totalCents ?? null;
  }

  function findExactAvailableVariant(variants, totalCents) {
    if (!Number.isInteger(totalCents)) return null;
    const expectedOptionValue = priceTierOptionValue(totalCents);
    const tier = Array.from(variants || []).filter((variant) => variant?.available === true && variant.option1 === expectedOptionValue);
    const matches = tier.filter((variant) => Number(variant.price) === totalCents);
    if (matches.length === 1) return matches[0];
    // The tier name carries the USD price, so it still finds the right variant if Shopify shows a converted
    // price (local currencies in Markets), where the price itself no longer equals the USD total.
    return tier.length === 1 ? tier[0] : null;
  }

  return Object.freeze({
    CONFIGURATION_VERSION,
    PRICING_VERSION,
    DATA,
    calculateOptionsPriceCents,
    calculatePriceBreakdown,
    calculateTotalCents,
    findExactAvailableVariant,
    getFoldedEdgeRule,
    getEdgeFinishingSurchargeCents,
    getOptionSurchargeCents,
    priceTierOptionValue,
    parseJsonScript
  });
});
